
import React, { useState, useRef, useEffect } from 'react';
import { ClassSchedule, Class } from '@/types/models';
import { Button } from '@/components/ui/button';
import { Pencil, Trash2, Clock, GripVertical, AlertTriangle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { ScheduleService } from '@/services/classService';
import EditScheduleDialog from './EditScheduleDialog';
import { cn } from '@/lib/utils';
import { ScheduleConflict } from '@/utils/scheduleConflicts';

interface ScheduleCardProps {
  schedule: ClassSchedule;
  classData?: Class;
  onScheduleUpdated: () => Promise<void>;
  onScheduleDeleted: () => Promise<void>;
  conflicts?: ScheduleConflict[];
  conflictSeverity?: 'none' | 'warning' | 'error';
  conflictMessage?: string;
  isDraggable?: boolean;
  onDragStart?: (e: React.DragEvent, scheduleId: string) => void;
  onDragEnd?: () => void;
}

const ScheduleCard: React.FC<ScheduleCardProps> = ({ 
  schedule, 
  classData,
  onScheduleUpdated,
  onScheduleDeleted,
  conflicts = [],
  conflictSeverity = 'none',
  conflictMessage = '',
  isDraggable = false,
  onDragStart,
  onDragEnd
}) => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  

  // Get color based on class level and conflicts
  const getLevelColor = (level?: string) => {
    // Override colors for conflicts
    if (conflictSeverity === 'error') {
      return 'bg-red-100 border-red-400 border-2';
    }
    if (conflictSeverity === 'warning') {
      return 'bg-yellow-100 border-yellow-400 border-2';
    }
    
    if (!level) return 'bg-gray-100 border-gray-200';
    
    // Map levels to colors
    const levelColors: Record<string, string> = {
      'Beginner': 'bg-blue-100 border-blue-200',
      'Intermediate': 'bg-green-100 border-green-200',
      'Advanced': 'bg-purple-100 border-purple-200',
      'All Levels': 'bg-amber-100 border-amber-200',
      'Performance': 'bg-rose-100 border-rose-200',
      'Competition': 'bg-teal-100 border-teal-200'
    };
    
    return levelColors[level] || 'bg-indigo-100 border-indigo-200';
  };
  
  // Format time to AM/PM
  const formatTime = (time?: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${hour > 12 ? hour - 12 : hour}:${minutes} ${ampm}`;
  };

  const handleDeleteSchedule = async () => {
    try {
      setIsDeleting(true);
      const result = await ScheduleService.deleteSchedule(schedule.id);
      if (result) {
        toast.success('Schedule deleted successfully');
        await onScheduleDeleted();
      } else {
        toast.error('Failed to delete schedule');
      }
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast.error('An error occurred while deleting the schedule');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div 
        ref={cardRef}
        className={cn(
          "p-2 rounded-md border text-sm relative group transition-all duration-200",
          getLevelColor(classData?.level),
          isDraggable && conflictSeverity === 'none' && "cursor-move"
        )}
        draggable={isDraggable && conflictSeverity === 'none'}
        onDragStart={(e) => {
          if (isDraggable && conflictSeverity === 'none' && onDragStart) {
            onDragStart(e, schedule.id);
          }
        }}
        onDragEnd={() => {
          if (isDraggable && onDragEnd) {
            onDragEnd();
          }
        }}
      >
        {/* Conflict Indicator */}
        {conflictSeverity !== 'none' && (
          <div className={cn(
            "absolute left-1 top-1 transition-opacity",
            conflictSeverity === 'error' ? 'text-red-600' : 'text-yellow-600'
          )}>
            {conflictSeverity === 'error' ? (
              <AlertCircle className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
          </div>
        )}
        
        {/* Drag Handle */}
        {isDraggable && conflictSeverity === 'none' && (
          <div className="absolute left-1 top-1 opacity-0 group-hover:opacity-60 transition-opacity cursor-grab">
            <GripVertical className="h-4 w-4 text-gray-400" />
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="absolute right-1 top-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setIsEditDialogOpen(true)}
            disabled={false}
          >
            <Pencil className="h-3 w-3" />
          </Button>
          <Button
            variant="destructive"
            size="icon"
            className="h-6 w-6"
            onClick={handleDeleteSchedule}
            disabled={isDeleting || false}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
        
        <div className="font-medium mb-1 pr-12">
          {classData?.name || 'Unknown Class'}
        </div>
        
        <div className="flex items-center text-xs mb-1">
          <Clock className="h-3 w-3 mr-1" />
          <span>
            {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
          </span>
        </div>
        
        {classData?.location && (
          <div className="text-xs opacity-80">
            Location: {classData.location}
          </div>
        )}
        
        {/* Conflict Message */}
        {conflictMessage && (
          <div className={cn(
            "text-xs mt-1 font-medium",
            conflictSeverity === 'error' ? 'text-red-700' : 'text-yellow-700'
          )}>
            ⚠️ {conflictMessage}
          </div>
        )}
      </div>
      
      {isEditDialogOpen && (
        <EditScheduleDialog
          open={isEditDialogOpen}
          schedule={schedule}
          onScheduleUpdated={onScheduleUpdated}
          onOpenChange={(isOpen) => setIsEditDialogOpen(isOpen)}
        />
      )}
    </>
  );
};

export default ScheduleCard;
